import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PATHS, ICONS } from 'src/common/constants';
import Actions from '../Actions';
import Button from '../Button';
import Empty from '../Empty';
import FontAwesomeIcon from '../Icon';
import Overlay from '../Overlay';
import Table from '../Table';
import Search from '../Search';
import Loader from '../Loader';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { selectSearch } from 'src/store/selectors/search';
import { selectFormLoader } from 'src/store/selectors/FormLoader';
import { onRequestPhoneCall } from 'src/helpers/validationHelper';
import {
  ContactFormActionsContext,
  useContactFormActionsFromContext,
} from 'src/SharedCommonContext/ContactFormContext';
import ContactForm from 'src/components/ContactForm/ContactForm';
import {
  fetchSuiteCounts,
  fetchPropertySuites,
} from 'src/store/actions/suites';
import { createSuiteUsersTableColumns } from 'src/components/SuiteUsersTable/helpers';
import { ITableColumn } from 'src/components/Table/types';
import { enrichContactDataForEdit } from 'src/utils/CommonHelpers/commonhelpers';
import {
  fetchContactForSuiteByIdFromAPI, sendActivationLink, sendResetPasswordLink,
} from 'src/utils/CommonHelpers/ContactAPIOperations/commonContactDataHelpers';
import {
  useContactFormSubmissionSuites
} from 'src/pages/SharedPageCustomHooks/UseContactSaveSuitesPage/useContactFormSubmissionSuites';

interface SuiteUsersTableProps {
  suite: any;
  property: any;
  contacts: any[];
  onDeleteContact: (data: any) => void;
  onfetchSuiteData: () => void;
  onSearch: (e: any) => void;
}

interface SuiteUsersTableProps {
  suite: any;
  property: any;
  contacts: any[];
  onDeleteContact: (data: any) => void;
  onfetchSuiteData: () => void;
  onSearch: (e: any) => void;
}

const SuiteUsersTable = ({
  suite,
  property,
  contacts,
  onDeleteContact,
  onfetchSuiteData,
  onSearch,
}: SuiteUsersTableProps) => {
  const [showOverlay, setShowOverlay] = useState(false);
  const [editingExistingContactInTable, setEditingExistingContactInTable] =
    useState<boolean>(false);
  const [editingContactRole, setEditingContactRole] = useState<string | null>(
    null
  );

  const dispatch = useDispatch();
  const user = useSelector(selectAuthenticatedUser);
  const search = useSelector(selectSearch);
  const loader = useSelector(selectFormLoader);

  const onFetchSuiteCounts = (suiteId: any, roleName: any) =>
    dispatch(fetchSuiteCounts(suiteId, roleName));

  const onFetchPropertySuites = (propertyId: any, userId: any, roleName: any) =>
    dispatch(fetchPropertySuites(propertyId, userId, roleName, false));

  const {
    clearContactFormData,
    setContactFormData,
    initializeContactFormDataFromEdit,
  }: ContactFormActionsContext = useContactFormActionsFromContext();

  const handleSetContactFormData = async (contactId: string) => {
    try {
      setEditingExistingContactInTable(true);

      const contactData: any = await fetchContactForSuiteByIdFromAPI(
        contactId,
        user.id,
        suite.id
      );

      const enrichedContactData = enrichContactDataForEdit(contactData);

      setEditingContactRole(contactData.roles?.[0]?.role?.name || null);

      initializeContactFormDataFromEdit({
        ...enrichedContactData,
        suiteId: suite.id,
        propertyId: property.propertyId,
      });

      setShowOverlay(true);
    } catch (error) {
      console.error('Failed to fetch contact details:', error);
    }
  };

  const suiteUsersTableConfig: ITableColumn[] = useMemo(
    () =>
      createSuiteUsersTableColumns({
        user,
        handleSetContactFormData,
        PATHS,
        ICONS,
        onRequestPhoneCall,
      }),
    [user, handleSetContactFormData]
  );

  const handleCloseContactForm = () => {
    setShowOverlay(false);
    clearContactFormData();
    setEditingExistingContactInTable(false);
    setEditingContactRole(null);
  };

  const handleOpenAddContactForm = () => {
    clearContactFormData();

    const initialProperty = [property];

    setContactFormData({
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      type: 'default',
      userRole: 'default',
      isPrimaryContact: false,
      suiteId: suite.id,
      propertyId: suite.propertyId,
      properties: initialProperty,
      Suites: [
        {
          Id: suite.id,
          name: suite.number || suite.name,
          propertyId: suite.propertyId,
        },
      ],
    });

    setShowOverlay(true);
  };
  
  const { handleSubmitContactForm } = useContactFormSubmissionSuites({
    propertyId: suite.propertyId,
    suiteId: suite.id,
    onSuccess: () => {
      onfetchSuiteData();
      setTimeout(() => {
        onFetchSuiteCounts(suite.id, user.roles[0].role.name);
        onFetchPropertySuites(
          property.propertyId,
          user.id,
          user.roles[0].role.name
        );
      }, 1000);
    },
    onClose: handleCloseContactForm,
  });

  const allowedRoles = editingExistingContactInTable
    ? [editingContactRole]
    : ['Home Owner', 'Occupant', 'Realtor'];

  let homeOwnerCheck =
    user &&
    user.roles &&
    user.roles.length > 0 &&
    (user.roles[0].role.name === 'Home Owner' ||
      user.roles[0].role.name === 'Occupant');
  let theme = localStorage.getItem('theme');

  return (
    <>
      {loader ? <Loader center={true} /> : null}
      <Actions compact>
        <Search
          size="small"
          placeholder="Search People..."
          onChange={onSearch}
          theme={undefined}
        />
        {(user.roles[0].role.name === 'Home Owner' && theme === 'Albaster') ||
        (homeOwnerCheck && theme === 'Rize') ||
        user.roles[0].role.name === 'Realtor' ? null : (
          <Button
            color="teal"
            label="Add Person"
            onClick={handleOpenAddContactForm}
            icon={
              <FontAwesomeIcon
                glyph="user-plus"
                weight="regular"
                size="small"
              />
            }
            size="small"
          />
        )}
      </Actions>
      {contacts.length > 0 ? (
        <>
          <Table columns={suiteUsersTableConfig} data={contacts} />
        </>
      ) : (
        <Empty
          direction="ttb"
          headline="No people yet"
          strapline="Add your people here so it's easier for you to manage."
          searchIsActive={search && search.contacts ? search.contacts : ''}
          cta={
            <>
              <Button
                color="teal"
                label="Add Person"
                onClick={handleOpenAddContactForm}
                icon={
                  <FontAwesomeIcon glyph="user" weight="light" size="small" />
                }
                size="small"
              />
            </>
          }
          image={<img alt="No people yet" src="/assets/empty/contacts.svg" />}
        />
      )}
      <Overlay
        showOverlay={showOverlay}
        content={
          <ContactForm
            onClose={handleCloseContactForm}
            onSubmit={handleSubmitContactForm}
            sendResetPasswordLinkToUser={sendResetPasswordLink}
            sendActivationLinkToUser={sendActivationLink}
            allowedRoles={allowedRoles}
            pageUsedFrom="Suites"
            editingExistingContactInTable={editingExistingContactInTable}
            currentSuiteIdForAddingExistingHomeowner={
              suite.number || suite.name
            }
          />
        }
      />
    </>
  );
};

export default SuiteUsersTable;
